import { Component, Prop, Vue } from 'vue-property-decorator'
import { createDecorator } from 'vue-class-component'
import { mapValues } from 'lodash'

interface Event {
  event: string
  target?: string
  options?: any
}

const PREFIX = 'w::'

/**
 * A decorator to add the method to the events option.
 * @param name The name of the event, uses the method name if none provided.
 */
export function Event (name?: string) {
  return createDecorator((options, key) => {
    const events = (options.events || (options.events = {}))
    events[name || key] = key
  })
}

/**
 * Add {PREFIX} to the event name.
 *
 * @param event The event name to prefix.
 *
 * @example
 *
 * prefixEvent('show::modal');
 * // => 'w::show::modal'
 */
const prefixEvent = (event: string) => `${PREFIX}${event}`

/**
 * @example
 * // register manually
 * created () {
 *  this.registerEvent('show::modal', this.show);
 * }
 *
 * @example
 * // register with decorator
 * import { Event } from './mixins/event'
 * @Event('show::modal')
 * show () {
 *   // code
 * }
 *
 * @example
 * // trigger an event
 * this.dispatchEvent('show::modal', '0');
 */
@Component
export default class extends Vue {
  @Prop({ type: String, default: '' })
  id!: string

  @Prop({ type: Object, default: () => ({}) })
  on!: Record<string, Event | Event[]>

  /**
   * Returns a dictionary of bound broadcastEvents functions.
   */
  get eventHandlers () {
    return mapValues(this.on,
        (_, key) => this.broadcastEvents.bind(this, key))
  }

  /**
   * Register all events in $options.
   */
  mounted () {
    const events = this.$options.events || {}
    Object.keys(events).forEach(event => {
      const handler = events[event]
      const callback = typeof handler === 'string'
        ? this.$options.methods &&
          this.$options.methods[handler] as (options?: any) => void
        : handler
      callback && this.registerEvent(event, callback.bind(this))
    })
  }

  /**
   * Register a callback to be executed if an event is broadcasted for {this.id}.
   *
   * @param event The name of the event to listen for.
   * @param callback The callback function to execute.
   */
  registerEvent (event: string, callback: (options?: any) => void) {
    const prefixedEvent = prefixEvent(event)
    const wrappedCallback = (id?: string, options?: any) =>
      (!id || id === this.id) && callback && callback(options)

    // Register event and attach destroy hook events
    this.$root.$on(prefixedEvent, wrappedCallback)
    this.$on('hook:beforeDestroy', () =>
      this.$root.$off(prefixedEvent, wrappedCallback)
    )

    // Return this for chaining
    return this
  }

  /**
   * Broadcast an event.
   *
   * @param event An event to broadcast.
   * @param target The id of the target widget.
   *
   * @example
   *
   * this.broadcastEvent('open::modal', 'myModal');
   */
  broadcastEvent (event: string, target?: string, options?: any) {
    this.$root.$emit(prefixEvent(event), target, options)

    // Return this for chaining
    return this
  }

  /**
   * Helper function to broadcast events in the events param.
   *
   * @param event The name of the event in the events param.
   *
   * @example
   *
   * // In a template
   * <button @click="broadcastEvents('click')">Submit</button>
   *
   * // Programmatically
   * this.broadcastEvents('click');
   */
  broadcastEvents (event: string) {
    let events = this.on[event] || []
    if (!Array.isArray(events)) {
      events = [events]
    }
    events.forEach(e => this.broadcastEvent(e.event, e.target, e.options))

    // Return this for chaining
    return this
  }
}
